/**
 * Which overlay owns the Escape key.
 *
 * THE BUG THIS EXISTS FOR. A glossary popover can be opened from inside the
 * kundli bhava popover — that nesting is deliberate, you tap a bhava, then tap
 * "Chandra" inside the explanation. Both components install their own
 * `document` keydown listener, and `document` dispatches an event to every
 * listener registered on it. So one Escape press closed BOTH layers at once,
 * and worse: the outer handler ran first and moved focus to the chart, then
 * the inner handler moved focus to its trigger button — which was inside the
 * outer popover that was already unmounting. Focus landed on <body>. A
 * keyboard user lost their reading context and their place in the page in a
 * single keystroke.
 *
 * `stopPropagation()` does not fix it: both listeners are on the SAME node, so
 * propagation never happens between them. `stopImmediatePropagation()` would
 * work only if the topmost overlay's listener were guaranteed to run first,
 * and it is not — listeners on a node fire in REGISTRATION order, so the
 * OUTER (older) overlay always hears Escape first. The ordering is exactly
 * backwards from what layering needs, which is why this has to be explicit.
 *
 * So overlays announce themselves instead. Each one takes a token while it is
 * open, and every Escape handler ignores the key unless it holds the top
 * token. Registration order stops mattering: the outer handler still runs
 * first, looks up, sees someone above it, and does nothing.
 *
 * Not a React context on purpose — it is read from inside a native event
 * listener, where the current value matters, not the value at the render that
 * installed the listener.
 */

const stack: symbol[] = [];

/** Claim the top of the stack. Call from an effect; release in its cleanup. */
export function pushOverlay(label: string): symbol {
  const token = Symbol(label);
  stack.push(token);
  return token;
}

/**
 * Release a token. Splices by identity rather than popping, because unmount
 * order is not guaranteed to mirror mount order — a parent overlay closing
 * while a child is still open would otherwise pop the child's token and leave
 * the parent's stranded, permanently deadening Escape for everything after.
 */
export function popOverlay(token: symbol): void {
  const i = stack.indexOf(token);
  if (i >= 0) stack.splice(i, 1);
}

/** True when this token is the topmost open overlay. */
export function isTopOverlay(token: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === token;
}

/** Test-only: the stack is module state and leaks between test cases. */
export function resetOverlayStack(): void {
  stack.length = 0;
}
